import React, { useState, useEffect, useRef, useCallback } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import styles from "./style.module.css";
import _ from "lodash";

// Feature flag: Set to true to enable exit animations when scrolling past elements
const ENABLE_EXIT_ANIMATIONS = true;

// Lerp factor - controls smoothness (0.08 = very smooth/laggy, 0.15 = snappier)
const LERP_FACTOR = 0.1;

// Update threshold - skip updates smaller than this to reduce micro-jitter
const UPDATE_THRESHOLD = 0.1;

// Tracking zone configuration (as percentage of viewport height)
// Progress starts when container top reaches TRACKING_START (higher = earlier start)
// Progress ends when container bottom reaches TRACKING_END (higher = earlier completion)
const TRACKING_ZONE_START = 0.85; // 85% down viewport (triggers early)
const TRACKING_ZONE_END = 0.3; // 30% from top (completes when container bottom reaches here)

// Threshold constants for step transitions
const STEP_THRESHOLDS = {
  step1: 0,
  step2: 33,
  step3: 60,
  complete: 95,
};

const VerticalProgress = () => {
  const [containerHeight, setContainerHeight] = useState(0);

  // Lerp animation state
  const [displayProgress, setDisplayProgress] = useState(0);
  const targetProgressRef = useRef(0);
  const displayProgressRef = useRef(0);

  // Persistent visibility state - once seen, stays visible
  const [hasSeenStep1, setHasSeenStep1] = useState(false);
  const [hasSeenStep2, setHasSeenStep2] = useState(false);
  const [hasSeenStep3, setHasSeenStep3] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);

  // Update "has been seen" state based on display progress
  useEffect(() => {
    if (displayProgress > STEP_THRESHOLDS.step1) setHasSeenStep1(true);
    if (displayProgress >= STEP_THRESHOLDS.step2) setHasSeenStep2(true);
    if (displayProgress >= STEP_THRESHOLDS.step3) setHasSeenStep3(true);
  }, [displayProgress]);

  // Scroll handler - calculates progress based on container's viewport position
  // Decoupled from sticky element positioning for independent control
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Define tracking zone boundaries
    const startY = viewportHeight * TRACKING_ZONE_START;
    const endY = viewportHeight * TRACKING_ZONE_END;

    // Total scroll distance: from when container top enters zone to when container bottom exits
    const totalScrollDistance = startY - endY + containerRect.height;

    // How far has container top scrolled past the start position?
    const distanceScrolled = startY - containerRect.top;

    // Calculate percentage (clamped 0-100)
    const percentage = Math.min(
      Math.max((distanceScrolled / totalScrollDistance) * 100, 0),
      100,
    );

    // Store raw float value - lerp will smooth it
    targetProgressRef.current = percentage;
  }, []);

  // Continuous lerp animation loop for smooth progress
  useEffect(() => {
    let animationId: number;

    const animate = () => {
      const target = targetProgressRef.current;
      const current = displayProgressRef.current;
      const diff = target - current;

      // Only update state if meaningful difference (reduces micro-jitter)
      if (Math.abs(diff) > UPDATE_THRESHOLD) {
        displayProgressRef.current += diff * LERP_FACTOR;
        setDisplayProgress(displayProgressRef.current);
      } else if (diff !== 0) {
        // Snap to target when very close (ensures we hit exactly 0% and 100%)
        displayProgressRef.current = target;
        setDisplayProgress(target);
      }

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Debounced resize handler
  const handleResize = useCallback(
    _.debounce(() => {
      const container = containerRef.current;
      if (container) {
        setContainerHeight(container.offsetHeight);
      }
    }, 250),
    [],
  );

  useEffect(() => {
    // Initial container height
    handleResize();

    // Add event listeners
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    // Initial calculation
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      handleResize.cancel();
    };
  }, [handleScroll, handleResize]);

  // Determine current step label for screen readers
  const getCurrentStepLabel = () => {
    if (displayProgress >= STEP_THRESHOLDS.complete)
      return "All steps complete";
    if (displayProgress >= STEP_THRESHOLDS.step3) return "Step 3 of 3";
    if (displayProgress >= STEP_THRESHOLDS.step2) return "Step 2 of 3";
    if (displayProgress > STEP_THRESHOLDS.step1) return "Step 1 of 3";
    return "Getting started";
  };

  // Calculate stage position with pixel snapping to prevent text blur
  // Convert percentage to pixels, round, then use pixel value
  const stageTopPixels =
    containerHeight > 0
      ? Math.round((displayProgress / 100) * containerHeight) - 40
      : 0;

  // Circle-to-bar animation: 24px wide at rest, shrinks to 20px on scroll
  // Transition happens over 0-5% progress
  const scrollStarted = displayProgress > 0.5;
  const widthTransitionProgress = Math.min(displayProgress / 5, 1); // 0 to 1 over 0-5%
  const progressBarWidth = 24 - widthTransitionProgress * 4; // 24px -> 20px
  const progressBarLeft = -2 + widthTransitionProgress * 2; // -2px -> 0px (centers the 24px in 20px container)

  return (
    <>
      <div className={styles.desktop}>
        <div ref={containerRef} className={styles.container}>
          {/* Progress bar - height animated via lerp, width shrinks from 24px to 20px */}
          <div
            className={styles.progressFill}
            style={{
              height: `${displayProgress}%`,
              width: `${progressBarWidth}px`,
              left: `${progressBarLeft}px`,
            }}
          />
          {/* Stage indicator - symmetric threshold at 4% for show/hide */}
          <div
            className={`${styles.stage} ${displayProgress > 4 ? styles.visible : ""}`}
            style={{ top: `${stageTopPixels}px` }}
            aria-live="polite"
            aria-atomic="true"
          >
            {displayProgress < STEP_THRESHOLDS.step2 && (
              <span
                className={styles.stepLabel}
                aria-label={getCurrentStepLabel()}
              >
                <span className={styles.stepText}>Step</span>
                <span className={styles.stepNumber}>1</span>
              </span>
            )}
            {displayProgress >= STEP_THRESHOLDS.step2 &&
              displayProgress < STEP_THRESHOLDS.step3 && (
                <span
                  className={styles.stepLabel}
                  aria-label={getCurrentStepLabel()}
                >
                  <span className={styles.stepText}>Step</span>
                  <span className={styles.stepNumber}>2</span>
                </span>
              )}
            {displayProgress >= STEP_THRESHOLDS.step3 &&
              displayProgress < STEP_THRESHOLDS.complete && (
                <span
                  className={styles.stepLabel}
                  aria-label={getCurrentStepLabel()}
                >
                  <span className={styles.stepText}>Step</span>
                  <span className={styles.stepNumber}>3</span>
                </span>
              )}
            {displayProgress >= STEP_THRESHOLDS.complete && (
              <span
                className={styles.stepLabel}
                role="img"
                aria-label="Launch, all steps complete"
                style={{ fontSize: "28px" }}
              >
                🚀
              </span>
            )}
          </div>
          <div ref={stickyRef} className={styles.progress} />
        </div>
        <div className={styles.contentContainer}>
          <div className={styles.leftContent}>
            {/* Step 1 - swoops from left */}
            {hasSeenStep1 && (
              <ProcessStep
                header={"We search for jobs daily and find your best matches."}
                lottieSource="../search.lottie"
                direction="left"
                delay={0}
              />
            )}
            {/* Step 3 - swoops from left */}
            {hasSeenStep3 && (
              <ProcessStep
                header={
                  "We submit applications and track everything for full visibility and transparency."
                }
                lottieSource="../dashboard.lottie"
                direction="left"
                delay={0}
              />
            )}
          </div>

          <div className={styles.rightContent}>
            {/* Step 2 - swoops from right */}
            {hasSeenStep2 && (
              <ProcessStep
                header={
                  "We tailor your resume to highlight your relevant experience — and draft authentic cover letters that tell your story."
                }
                lottieSource="../writing.lottie"
                direction="right"
                delay={0}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles.mobile}>
        <ProcessStep
          header={"We search for jobs daily and find your best matches."}
          lottieSource="../search.lottie"
          direction="left"
          delay={0}
        />
        <ProcessStep
          header={
            "We tailor your resume to highlight your relevant experience — and draft authentic cover letters that tell your story."
          }
          lottieSource="../writing.lottie"
          direction="right"
          delay={100}
        />
        <ProcessStep
          header={
            "We submit applications and track everything for full visibility and transparency."
          }
          lottieSource="../dashboard.lottie"
          direction="left"
          delay={200}
        />
      </div>
    </>
  );
};

export default VerticalProgress;

function ProcessStep({
  lottieSource = "../animation.lottie",
  header = "Lottie Animation",
  delay = 0,
  direction = "left",
}) {
  const [isInView, setIsInView] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (ENABLE_EXIT_ANIMATIONS) {
          // Toggle visibility based on intersection state
          setIsInView(entry.isIntersecting);
        } else {
          // Original behavior: animate in once, stay visible
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(element);
          }
        }
      },
      {
        rootMargin: "50px 0px", // Trigger 50px before entering viewport
        threshold: 0.1,
      },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const directionClass =
    direction === "right" ? styles.fromRight : styles.fromLeft;

  return (
    <div
      ref={elementRef}
      className={`${styles.lottieComponent} ${directionClass} ${isInView ? styles.visible : ""}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className={styles.animationFrame} aria-hidden="true">
        <DotLottieReact
          src={lottieSource}
          loop
          width={300}
          height={300}
          autoplay
        />
      </div>
      <p>{header}</p>
    </div>
  );
}
